import type { ColDef, GridApi, SetFilterHandler } from "ag-grid-community"
import { useEffect, useLayoutEffect, useRef, useState } from "react"

import { detailGridApis, refreshColumnValues } from "~/utils/ag-utils"
import { filterDefined } from "~/utils/tsUtils"

/**
 * Columns whose definitions stay stable while the data they read outside the rows changes,
 * paired with that data's version so the two can't be handed to a table apart.
 */
export type VersionedColumns<TData> = {
  columns: ColDef<TData>[]
  /** Changes identity whenever the data behind `columns` changes; the table repaints on it. */
  dataVersion: unknown
}

/** Module-level so the reader closes over the ref alone, not the render that created it. */
function readRef<T>(ref: { readonly current: T }): () => T {
  return () => ref.current
}

export type LiveReader<T> = () => T

/**
 * A stable reader of the latest `value`, for column callbacks that read data when AG Grid
 * evaluates them rather than when they are built. A layout effect runs before the passive effects
 * in which ag-grid-react applies new props and the tables repaint, so no callback reads stale data.
 */
export function useLiveReader<T>(value: T): LiveReader<T> {
  const ref = useRef(value)
  useLayoutEffect(() => {
    ref.current = value
  }, [value])
  const [read] = useState(() => readRef(ref))
  return read
}

const usesSetFilter = (filter: ColDef["filter"]) =>
  filter === "agSetColumnFilter" || filter === true

/**
 * Refreshes `columns` with the data they read now. Set filters rebuild their value lists only when
 * rows load or a cell in their column is edited, so they are refreshed here too.
 */
export function refreshColumnsAndFilters<TData>(api: GridApi, columns: ColDef<TData>[]) {
  refreshColumnValues(api, filterDefined(columns.map((column) => column.colId)))
  for (const column of columns) {
    if (!column.colId || !usesSetFilter(column.filter)) continue
    api.getColumnFilterHandler<Partial<SetFilterHandler>>(column.colId)?.refreshFilterValues?.()
  }
}

/** The columns of every source whose data version changed since `previousVersions`. */
export function changedColumns<TData>(
  sources: readonly (VersionedColumns<TData> | undefined)[],
  previousVersions: readonly unknown[],
): ColDef<TData>[] {
  return sources.flatMap((source, i) =>
    source && source.dataVersion !== previousVersions[i] ? source.columns : [],
  )
}

/**
 * Repaints, in one pass per grid, the columns of every source whose `dataVersion` changed in a
 * commit, in the grid and its open detail grids. A grid created after the data arrived already
 * reads it, so the first render refreshes nothing. Sources keep their position across renders;
 * an absent one is `undefined`.
 */
export function useRefreshColumnsOnDataChange<TData>(
  gridRef: { readonly current: { readonly api?: GridApi | null } | null } | undefined,
  sources: readonly (VersionedColumns<TData> | undefined)[],
) {
  const versions = sources.map((source) => source?.dataVersion)
  const previousVersions = useRef(versions)
  useEffect(() => {
    const columns = changedColumns(sources, previousVersions.current)
    previousVersions.current = versions
    const api = gridRef?.current?.api
    if (columns.length === 0 || !api || api.isDestroyed()) return
    for (const gridApi of [api, ...detailGridApis(api)]) refreshColumnsAndFilters(gridApi, columns)
  })
}
